/**
 * SEARCH RESULTS PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  UserIcon,
  FolderIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { apiClient } from '../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Screen, Section, Panel, Badge } from '../design/primitives';
import { Alert } from '../components/ui';

interface SearchResult {
  type: 'person' | 'project' | 'business' | 'task';
  id: number;
  title: string;
  subtitle?: string;
  description?: string;
  status?: string;
  date?: string;
}

const SearchResultsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResults = async () => {
      if (!query.trim()) {
        setResults([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let peopleResults: any[] = [];
        let projectResults: any[] = [];
        let businessResults: any[] = [];
        let taskResults: any[] = [];

        try {
          const peopleRes = await apiClient.get(`/api/people/?search=${encodeURIComponent(query)}`);
          peopleResults = peopleRes.data;
        } catch (err) {
          console.error('Error fetching people:', err);
        }

        try {
          const projectsRes = await apiClient.get(`/api/projects/?search=${encodeURIComponent(query)}`);
          projectResults = projectsRes.data;
        } catch (err) {
          console.error('Error fetching projects:', err);
        }

        try {
          const businessesRes = await apiClient.get(`/api/businesses/?search=${encodeURIComponent(query)}`);
          businessResults = businessesRes.data;
        } catch (err) {
          console.error('Error fetching businesses:', err);
        }

        try {
          const tasksRes = await apiClient.get(`/api/tasks/?search=${encodeURIComponent(query)}`);
          taskResults = tasksRes.data;
        } catch (err) {
          console.error('Error fetching tasks:', err);
        }

        const formattedResults: SearchResult[] = [
          ...peopleResults.map((person: any) => ({
            type: 'person' as const,
            id: person.id,
            title: person.name,
            subtitle: person.relationship,
            description: person.notes,
            date: person.last_interaction
          })),
          ...projectResults.map((project: any) => ({
            type: 'project' as const,
            id: project.id,
            title: project.name,
            description: project.description,
            status: project.status,
            date: project.created_at
          })),
          ...businessResults.map((business: any) => ({
            type: 'business' as const,
            id: business.id,
            title: business.name,
            description: business.description,
            status: business.status,
            date: business.created_at
          })),
          ...taskResults.map((task: any) => ({
            type: 'task' as const,
            id: task.id,
            title: task.description,
            subtitle: task.is_done ? 'Completed' : 'Pending',
            description: task.project?.name || task.business?.name,
            status: task.importance,
            date: task.due_date || task.created_at
          }))
        ];

        setResults(formattedResults);
      } catch (err) {
        console.error("Error in search:", err);
        setError("Failed to load search results. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [query]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return null;
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch {
      return "Invalid Date";
    }
  };

  const getIcon = (type: string) => {
    const iconStyle = {
      width: '20px',
      height: '20px',
      color: COLORS.accent.default,
    };

    switch (type) {
      case 'person':
        return <UserIcon style={iconStyle} />;
      case 'project':
        return <FolderIcon style={iconStyle} />;
      case 'business':
        return <BuildingOfficeIcon style={iconStyle} />;
      case 'task':
        return <CheckCircleIcon style={iconStyle} />;
      default:
        return null;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'person':
        return 'Person';
      case 'project':
        return 'Project';
      case 'business':
        return 'Business';
      case 'task':
        return 'Task';
      default:
        return type;
    }
  };

  const handleResultClick = (result: SearchResult) => {
    switch (result.type) {
      case 'person':
        navigate(`/people/${result.id}`);
        break;
      case 'project':
        navigate(`/projects/${result.id}`);
        break;
      case 'business':
        navigate(`/businesses/${result.id}`);
        break;
      case 'task':
        navigate(`/tasks`);
        break;
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: SPACING[24],
        }}
      >
        <ArrowPathIcon
          style={{
            width: '24px',
            height: '24px',
            color: COLORS.text.tertiary,
            animation: 'spin 1s linear infinite',
          }}
        />
      </div>
    );
  }

  return (
    <Screen>
      <div
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
        }}
      >
      <div style={{ marginBottom: SPACING[24] }}>
        <h1
          style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h1,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
          }}
        >
          Search Results for "{query}"
        </h1>
        <p
          style={{
            marginTop: SPACING[8],
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.text.secondary,
            margin: `${SPACING[8]} 0 0 0`,
          }}
        >
          Found {results.length} results
        </p>
      </div>

      {error && (
        <Alert variant="error">{error}</Alert>
      )}

      {!loading && !error && results.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: `${SPACING[48]} 0`,
          }}
        >
          <p
            style={{
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.text.secondary,
              margin: 0,
            }}
          >
            No results found for "{query}"
          </p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
        {results.map((result) => (
          <div
            key={`${result.type}-${result.id}`}
            style={{
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
              e.currentTarget.style.borderColor = COLORS.border.hover;
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
              e.currentTarget.style.borderColor = COLORS.border.default;
            }}
            onClick={() => handleResultClick(result)}
          >
            <Panel
              style={{
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: SPACING[16],
              }}
            >
              <div
                style={{
                  padding: SPACING[8],
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {getIcon(result.type)}
              </div>
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: SPACING[12],
                  }}
                >
                  <h3
                    style={{
                      fontFamily: TYPOGRAPHY.headings.fontFamily,
                      fontSize: TYPOGRAPHY.headings.sizes.h3,
                      fontWeight: TYPOGRAPHY.headings.weight,
                      color: COLORS.text.primary,
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {result.title}
                  </h3>
                  <Badge>{getTypeLabel(result.type)}</Badge>
                </div>
                {result.subtitle && (
                  <p
                    style={{
                      marginTop: SPACING[4],
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.text.secondary,
                      margin: `${SPACING[4]} 0 0 0`,
                    }}
                  >
                    {result.subtitle}
                  </p>
                )}
                {result.description && (
                  <p
                    style={{
                      marginTop: SPACING[8],
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.text.tertiary,
                      margin: `${SPACING[8]} 0 0 0`,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {result.description}
                  </p>
                )}
                {result.date && (
                  <p
                    style={{
                      marginTop: SPACING[8],
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.tiny,
                      color: COLORS.text.tertiary,
                      margin: `${SPACING[8]} 0 0 0`,
                    }}
                  >
                    {formatDate(result.date)}
                  </p>
                )}
              </div>
            </div>
            </Panel>
          </div>
        ))}
      </div>
      </div>
    </Screen>
  );
};

export default SearchResultsPage;
